import axios, { AxiosRequestConfig, AxiosError } from 'axios';

const API_BASE_URL = 'https://cary-troubleshooting-guides.thankfulbay-b6ecffb7.northeurope.azurecontainerapps.io';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth context for token management
let authContext: {
  refreshAuthToken: () => Promise<string | null>;
  logout: () => Promise<void>;
  token: string | null;
} | null = null;

export const setAuthContext = (context: typeof authContext) => {
  authContext = context;
};

// Request interceptor to add auth header
apiClient.interceptors.request.use(
  (config) => {
    if (authContext?.token) {
      config.headers.Authorization = `Bearer ${authContext.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _retryCount?: number };

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      // Only retry up to 2 times
      if (originalRequest._retryCount <= 2 && authContext?.refreshAuthToken) {
        try {
          const newToken = await authContext.refreshAuthToken();

          if (newToken) {
            // Update the token in auth context
            authContext.token = newToken;

            // Update the authorization header for the retry
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          // Only logout if we've tried multiple times
          if (originalRequest._retryCount >= 2 && authContext?.logout) {
            await authContext.logout();
          }
        }
      }
    }

    // Handle 403 Forbidden - might also indicate token issues
    if (error.response?.status === 403 && !originalRequest._retry) {
      if (authContext?.refreshAuthToken) {
        try {
          const newToken = await authContext.refreshAuthToken();

          if (newToken) {
            originalRequest._retry = true;
            authContext.token = newToken;

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            return apiClient(originalRequest);
          }
        } catch (refreshError) {
          // Token refresh failed, will reject with original error
        }
      }
    }

    return Promise.reject(error);
  }
);

// Custom instance for Orval
export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  return apiClient(config).then(({ data }) => data);
};